/**
 * Tiers List Context
 *
 * Provides shared state and configuration to TierCard components
 * to eliminate prop drilling.
 */
import { createContext, useContext, type FC, type ReactNode } from 'react';

export interface BadgeTexts {
	active: string;
	achieved: string;
}

export interface ButtonTexts {
	join: string;
	signIn: string;
}

export interface TierBadgeStyles {
	active?: React.CSSProperties;
	achieved?: React.CSSProperties;
}

export interface TiersListContextValue {
	/** Position of the user's current tier (lowest = 0); null when not enrolled */
	currentTierPosition: number | null;

	/** Heading level for card labels (1-6) */
	labelLevel: number;

	/** Whether to show tier descriptions */
	showDescription: boolean;

	/** Whether to show tier perks */
	showPerks: boolean;

	/** Image height in px */
	imageHeight: number;

	/** Image fit mode */
	imageFit: 'contain' | 'cover' | 'fill';

	/** Badge text customizations */
	badgeTexts: BadgeTexts;

	/** Inline styles for status badges */
	badgeStyles: TierBadgeStyles;
}

export interface TiersListProviderProps {
	children: ReactNode;
	value: TiersListContextValue;
}

const TiersListContext = createContext<TiersListContextValue | undefined>(undefined);

export const TiersListProvider: FC<TiersListProviderProps> = ({ children, value }) => {
	return <TiersListContext.Provider value={value}>{children}</TiersListContext.Provider>;
};

export function useTiersListContext(): TiersListContextValue {
	const context = useContext(TiersListContext);

	if (context === undefined) {
		throw new Error('useTiersListContext must be used within a TiersListProvider');
	}

	return context;
}
